import { toast } from "sonner";
import type { BananImageResult, KlingVideoResult } from "./adsUtils";
import { persistAdArtifact } from "./persistAdArtifact";
import { useAdsWorkspaceStore } from "../store/useAdsWorkspaceStore";

interface BananJobResultLike {
  imageUrl?: string;
  imageUrls?: string[];
}

interface KlingJobResultLike {
  videoUrl?: string;
  thumbnailUrl?: string;
}

export function handleKlingJobComplete(jobId: string, result: KlingJobResultLike) {
  const store = useAdsWorkspaceStore.getState();
  const pendingJob = store.pendingKlingJobs.find((j) => j.jobId === jobId);
  if (pendingJob?.provider === "heygen") {
    // The global ad-jobs poller owns HeyGen adclone completion so it can
    // persist provider metadata and publish the clone editor handoff event.
    return;
  }
  store.completeKlingJob(jobId, result.videoUrl, result.thumbnailUrl);

  toast.success("Video generation complete", {
    description: pendingJob?.productName
      ? `Video for "${pendingJob.productName}" is ready in Assets.`
      : "Your video ad is ready in the Assets tab.",
  });

  const workspaceId = store.scopeWorkspaceId;
  if (workspaceId) {
    const videoResult: KlingVideoResult = {
      id: jobId,
      status: "done",
      video_output: result.videoUrl,
      video_thumbnail: result.thumbnailUrl,
    };
    void persistAdArtifact({
      adType: "video",
      result: videoResult,
      workspaceId,
      label: "Kling AI",
      extraMetadata: {
        source: "kling",
        jobId,
        ...(pendingJob?.productId ? { productId: pendingJob.productId } : {}),
        ...(pendingJob?.productName ? { productName: pendingJob.productName } : {}),
        ...(pendingJob?.sourceImageJobId ? { sourceImageJobId: pendingJob.sourceImageJobId } : {}),
        ...(pendingJob?.sourceImageUrl ? { sourceImageUrl: pendingJob.sourceImageUrl } : {}),
        ...(pendingJob?.sourceResearchAngle ? { sourceResearchAngle: pendingJob.sourceResearchAngle } : {}),
        ...(pendingJob?.sourceVideoScriptAngle ? { sourceVideoScriptAngle: pendingJob.sourceVideoScriptAngle } : {}),
        ...(pendingJob?.targetProblem ? { targetProblem: pendingJob.targetProblem } : {}),
      },
    });
  }
}

export function handleBananJobComplete(jobId: string, result: BananJobResultLike) {
  const store = useAdsWorkspaceStore.getState();
  const pendingJob = store.pendingBananJobs.find((j) => j.jobId === jobId);
  store.completeBananJob(jobId, result.imageUrl, result.imageUrls);

  if (!pendingJob) return;

  const isBatch = (result.imageUrls?.length ?? 0) > 1;

  toast.success("Image generation complete", {
    description: isBatch
      ? `${result.imageUrls!.length} images are ready in the Assets tab.`
      : pendingJob.productName
        ? `Image for "${pendingJob.productName}" is ready in Assets.`
        : "Your image ad is ready in the Assets tab.",
  });

  const workspaceId = store.scopeWorkspaceId;
  if (!workspaceId) return;

  if (isBatch && result.imageUrls) {
    for (let i = 0; i < result.imageUrls.length; i += 1) {
      const lineage = pendingJob.batchItems?.[i];
      const imageResult: BananImageResult = {
        id: `${jobId}_${i}`,
        status: "done",
        output: result.imageUrls[i],
      };
      void persistAdArtifact({
        adType: "image",
        result: imageResult,
        workspaceId,
        label: "Banan AI",
        extraMetadata: {
          source: "banan",
          jobId,
          batchIndex: i,
          ...(pendingJob.productId ? { productId: pendingJob.productId } : {}),
          ...(pendingJob.productName ? { productName: pendingJob.productName } : {}),
          ...(lineage?.sourceResearchAngle ? { sourceResearchAngle: lineage.sourceResearchAngle } : {}),
          ...(lineage?.targetProblem ? { targetProblem: lineage.targetProblem } : {}),
          ...(lineage?.overlayText ? { overlayText: lineage.overlayText } : {}),
          ...(lineage?.ugcSceneSlot ? { ugcSceneSlot: lineage.ugcSceneSlot } : {}),
          ...(lineage?.ugcSceneIndex !== undefined ? { ugcSceneIndex: lineage.ugcSceneIndex } : {}),
          ...(lineage?.ugcVideoIndex !== undefined ? { ugcVideoIndex: lineage.ugcVideoIndex } : {}),
          ...(lineage?.sourceVideoScriptAngle ? { sourceVideoScriptAngle: lineage.sourceVideoScriptAngle } : {}),
        },
      });
    }
    return;
  }

  const imageResult: BananImageResult = {
    id: jobId,
    status: "done",
    output: result.imageUrl ?? result.imageUrls?.[0],
  };
  void persistAdArtifact({
    adType: "image",
    result: imageResult,
    workspaceId,
    label: "Banan AI",
    extraMetadata: {
      source: "banan",
      jobId,
      ...(pendingJob.productId ? { productId: pendingJob.productId } : {}),
      ...(pendingJob.productName ? { productName: pendingJob.productName } : {}),
      ...(pendingJob.sourceResearchAngle ? { sourceResearchAngle: pendingJob.sourceResearchAngle } : {}),
      ...(pendingJob.targetProblem ? { targetProblem: pendingJob.targetProblem } : {}),
      ...(pendingJob.overlayText ? { overlayText: pendingJob.overlayText } : {}),
    },
  });
}
